'use client'

import { usePathname } from 'next/navigation'
import React, { useEffect } from 'react'

import { useRecordTouchEventApi } from '@/api/useRecordTouchEventApi'
import useAnonymousIdStore from '@/stores/useAnonymousIdStore'
import useAttributionStore from '@/stores/useAttributionStore'
import { extractMarketingParams, hasMarketingParams } from '@/utils/marketingParams'
import { buildTouchEventInput } from '@/utils/touchEvent'

/** Session set of click signatures we've already recorded a touch for. */
const TOUCH_SIGNATURES_KEY = 'ss_touch_signatures'

// Click-id params that distinguish one ad landing from another.
const CLICK_SIGNATURE_PARAMS = ['fbclid', 'gclid', 'gbraid', 'wbraid', 'msclkid', 'ttclid']

/** A stable signature of the current landing's click ids ('' for a direct/organic visit). */
const computeClickSignature = (search: string): string => {
  const params = new URLSearchParams(search)
  return CLICK_SIGNATURE_PARAMS.reduce<string[]>((acc, key) => {
    const value = params.get(key)
    if (value) acc.push(`${key}=${value}`)
    return acc
  }, [])
    .sort()
    .join('&')
}

const readSignatures = (): string[] => {
  try {
    return JSON.parse(window.sessionStorage.getItem(TOUCH_SIGNATURES_KEY) || '[]') as string[]
  } catch {
    return []
  }
}

/**
 * Landing-attribution entry point (mounted inside the providers, fires app-wide).
 *
 *  1. Captures the URL marketing params into the client attribution store
 *     (first/last-touch memory, read later at order submit).
 *  2. Records a backend touch on the session-start landing and again whenever a
 *     new click signal (fbclid/gclid/...) appears, reusing RudderStack's
 *     anonymousId as the stitch key. Fire-and-forget; never affects the page.
 *
 * (Booking-only params — clickId/coupon — stay in SearchParamSync.)
 */
const AttributionCapture: React.FC = () => {
  const captureAttribution = useAttributionStore((store) => store.captureAttribution)
  const anonymousId = useAnonymousIdStore((store) => store.anonymousId)
  const { mutate } = useRecordTouchEventApi()
  const pathname = usePathname()

  // 1. Capture marketing URL params into the client store (also on client nav).
  useEffect(() => {
    if (typeof window === 'undefined') return

    const marketing = extractMarketingParams(new URLSearchParams(window.location.search))
    if (hasMarketingParams(marketing)) {
      captureAttribution(marketing, { path: window.location.pathname })
    }
  }, [captureAttribution, pathname])

  // 2. Record a touch on session-start + each new click signal, once
  //    RudderStack's anonymousId is ready.
  useEffect(() => {
    if (typeof window === 'undefined' || !anonymousId) return

    const signature = computeClickSignature(window.location.search)
    const recorded = readSignatures()
    const isSessionStart = recorded.length === 0
    const isNewSignal = signature !== '' && !recorded.includes(signature)
    if (!isSessionStart && !isNewSignal) return

    recorded.push(signature)
    window.sessionStorage.setItem(TOUCH_SIGNATURES_KEY, JSON.stringify(recorded))
    mutate(buildTouchEventInput({ anonymousId }), { onError: () => undefined })
  }, [anonymousId, mutate, pathname])

  return null
}

export default AttributionCapture
